/** 设备处分只读列表与人工解封，设备与账号处分分别维护。 */
import { type DeviceSanctionResponse, DeviceSanctionRevisionSchema } from '@novel-admin/shared';
import { onlineManager, useQueryClient } from '@tanstack/react-query';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AdminApiError,
  AdminNetworkError,
  AdminRequestAbortedError,
  UnexpectedAdminApiResponseError,
} from '~/api/errors';
import { useAppScope } from '~/app/app-scope-store';
import { useDeviceSanctions, useReleaseDeviceSanction } from '~/data/device-sanction-queries';
import { useAuthStore } from '~/stores/auth-store';
import { Dialog } from '~/ui/Dialog';
import { toast } from '~/ui/Toast';

/** revision只验证契约形态，不解析、不构造，也不按时间戳替代。 */
function hasValidRevision(row: DeviceSanctionResponse): boolean {
  return DeviceSanctionRevisionSchema.safeParse(row.revision).success;
}

export function DeviceSanctionsDialog({ onClose }: { onClose: () => void }) {
  const appId = useAppScope((state) => state.appId);
  const role = useAuthStore((state) => state.user?.role);
  const [deviceId, setDeviceId] = useState('');
  const [status, setStatus] = useState('banned');
  const [page, setPage] = useState(1);
  const [target, setTarget] = useState<
    (DeviceSanctionResponse & { scope: string; appScope: string }) | null
  >(null);
  const targetRef = useRef<typeof target>(null);
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const records = useDeviceSanctions(appId, deviceId, status, page);
  const release = useReleaseDeviceSanction();
  const canRelease = role === 'root' || role === 'admin' || role === 'operator';
  const scope = JSON.stringify([appId, deviceId, status, page]);
  const queryKey = ['device-sanctions', appId, deviceId, status, page];
  const listReady =
    records.isFetchedAfterMount &&
    records.fetchStatus === 'idle' &&
    !records.isError &&
    !!records.data &&
    queryClient.getQueryState(queryKey)?.isInvalidated === false;
  const clearConfirmation = useCallback(() => {
    // 同步清除引用，防筛选变化同一turn内旧按钮事件仍拿到已失效目标。
    targetRef.current = null;
    setTarget(null);
    setReason('');
  }, []);
  // biome-ignore lint/correctness/useExhaustiveDependencies: App变化是分页重置边界。
  useEffect(() => {
    setPage(1);
  }, [appId]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: 筛选/分页scope变化必须丢弃确认状态。
  useEffect(() => {
    clearConfirmation();
  }, [scope, clearConfirmation]);
  const visibleTarget =
    target?.scope === scope
      ? records.data?.data.find(
          (row) =>
            hasValidRevision(row) &&
            row.revision === target.revision &&
            row.id === target.id &&
            row.app_id === target.app_id &&
            row.device_id === target.device_id &&
            row.updated_at === target.updated_at &&
            row.sanctioned_at === target.sanctioned_at &&
            row.status === 'banned' &&
            (appId === 'all' || row.app_id === appId),
        )
      : undefined;
  useEffect(() => {
    if (target && (!listReady || !visibleTarget)) clearConfirmation();
  }, [target, listReady, visibleTarget, clearConfirmation]);
  const confirmedTarget = listReady ? visibleTarget : undefined;
  const canConfirmCurrentTarget = () => {
    if (
      !target ||
      !hasValidRevision(target) ||
      targetRef.current !== target ||
      target.scope !== scope ||
      useAppScope.getState().appId !== target.appScope ||
      !canRelease ||
      release.isPending
    )
      return false;
    // 查询可能已在同turn开始刷新，不能仅凭上一React渲染的isFetching判断。
    const current = queryClient.getQueryState<{ data: DeviceSanctionResponse[] }>(queryKey);
    return (
      current?.status === 'success' &&
      current.fetchStatus === 'idle' &&
      !current.isInvalidated &&
      current.data?.data.some(
        (row) =>
          hasValidRevision(row) &&
          row.revision === target.revision &&
          row.id === target.id &&
          row.app_id === target.app_id &&
          row.device_id === target.device_id &&
          row.updated_at === target.updated_at &&
          row.sanctioned_at === target.sanctioned_at &&
          row.status === 'banned',
      ) === true
    );
  };
  return (
    <Dialog
      title="设备处分"
      subtitle="解封仅解除设备处分，不解除账号封禁或恢复会话。"
      onClose={() => {
        if (!release.isPending) onClose();
      }}
      width={800}
    >
      <div style={{ padding: 20 }}>
        <div className="hstack" style={{ gap: 8, marginBottom: 14 }}>
          <input
            className="input"
            placeholder="完整设备 ID"
            value={deviceId}
            onChange={(event) => {
              clearConfirmation();
              setDeviceId(event.target.value);
              setPage(1);
            }}
          />
          <select
            className="input"
            aria-label="设备处分状态"
            value={status}
            onChange={(event) => {
              clearConfirmation();
              setStatus(event.target.value);
              setPage(1);
            }}
          >
            <option value="">全部状态</option>
            <option value="banned">已封禁</option>
            <option value="active">已解除</option>
          </select>
        </div>
        {records.isError ? (
          <div role="alert">设备处分读取失败，请重试。</div>
        ) : records.isLoading ? (
          <div>加载中…</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>设备 ID</th>
                <th>处分状态</th>
                <th>原因</th>
                <th>处分时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {records.data?.data.map((row) => (
                <tr key={row.id}>
                  <td className="mono">{row.device_id}</td>
                  <td>{row.status === 'banned' ? '已封禁' : '已解除'}</td>
                  <td>{row.reason ?? '—'}</td>
                  <td>{row.sanctioned_at ?? '—'}</td>
                  <td>
                    <button
                      className="btn btn-sm"
                      disabled={
                        !canRelease ||
                        !listReady ||
                        !hasValidRevision(row) ||
                        row.status !== 'banned' ||
                        release.isPending
                      }
                      onClick={() => {
                        if (
                          !listReady ||
                          !canRelease ||
                          !hasValidRevision(row) ||
                          row.status !== 'banned' ||
                          release.isPending
                        )
                          return;
                        const selected = { ...row, scope, appScope: appId };
                        targetRef.current = selected;
                        setTarget(selected);
                        setReason('');
                      }}
                    >
                      解封设备
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="hstack" style={{ gap: 8, marginTop: 14 }}>
          <button
            className="btn btn-sm"
            disabled={page === 1}
            onClick={() => {
              clearConfirmation();
              setPage((value) => value - 1);
            }}
          >
            上一页
          </button>
          <span>
            第 {page} 页 · 共 {records.data?.total ?? 0} 条
          </span>
          <button
            className="btn btn-sm"
            disabled={page * 20 >= (records.data?.total ?? 0)}
            onClick={() => {
              clearConfirmation();
              setPage((value) => value + 1);
            }}
          >
            下一页
          </button>
        </div>
        {confirmedTarget && target && (
          <div className="card" style={{ padding: 14, marginTop: 14 }}>
            <p>
              确认目标设备：<span className="mono">{target.device_id}</span> · App：
              <span className="mono">{target.app_id}</span>
            </p>
            <input
              className="input"
              aria-label="设备解封原因"
              maxLength={500}
              placeholder="填写解封原因"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
            <button
              className="btn btn-sm"
              disabled={!canRelease || !confirmedTarget || !reason.trim() || release.isPending}
              onClick={() => {
                if (!canConfirmCurrentTarget()) {
                  clearConfirmation();
                  return;
                }
                // 同步拒绝离线点击，关闭/重连后不让mutation微任务补发旧解封决定。
                if (!onlineManager.isOnline()) {
                  toast('网络不可用，本次解封未发送；恢复后请重新核验并提交。', { tone: 'neg' });
                  return;
                }
                release.mutate(
                  { id: target.id, reason: reason.trim(), expected_revision: target.revision },
                  {
                    onSuccess: () => {
                      clearConfirmation();
                      toast('设备已解封', { tone: 'pos' });
                    },
                    onError: (error) => {
                      const resultUnknown =
                        error instanceof AdminNetworkError ||
                        error instanceof UnexpectedAdminApiResponseError ||
                        (error instanceof AdminApiError &&
                          error.httpStatus >= 500 &&
                          error.httpStatus < 600) ||
                        (error instanceof AdminRequestAbortedError && error.serverStateUnknown);
                      const revisionConflict =
                        error instanceof AdminApiError && error.code === 'RESOURCE_CONFLICT';
                      if (resultUnknown || revisionConflict) {
                        // 结果未知或版本冲突都丢弃本次决定，重读后必须重新选择当前处分。
                        clearConfirmation();
                        void queryClient.invalidateQueries({ queryKey: ['device-sanctions'] });
                      }
                      toast(
                        resultUnknown
                          ? '解封结果未知，请刷新设备处分确认后再操作。'
                          : revisionConflict
                            ? '设备处分已更新，请重新读取并选择后确认。'
                            : error instanceof Error
                              ? error.message
                              : '解封失败',
                        { tone: 'neg' },
                      );
                    },
                  },
                );
              }}
            >
              确认解封
            </button>
            <button
              className="btn btn-sm btn-ghost"
              disabled={release.isPending}
              onClick={clearConfirmation}
            >
              取消
            </button>
          </div>
        )}
      </div>
    </Dialog>
  );
}
